import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import ScorecardSheet from "../components/ScoreCardSheet";
import PayoutTablePrint from "../components/PayoutTablePrint";
import apiClient from "../utils/apiClient";

const PrintScorecardPage = () => {
  const [orientation, setOrientation] = useState("landscape"); // Default to landscape
  const [searchParams] = useSearchParams();
  const { state } = useLocation();

  // State for fetched data
  const [eventDetails, setEventDetails] = useState(state?.eventDetails || null);
  const [eventPlayers, setEventPlayers] = useState(state?.eventPlayers || []);
  const [scorecard, setScorecard] = useState(state?.scorecard || []);
  const [loading, setLoading] = useState(!state?.eventDetails);
  const [error, setError] = useState(null);

  // Fetch event data if not provided via state
  useEffect(() => {
    const eventId = searchParams.get("eventId");

    // If we already have data from state, don't fetch
    if (state?.eventDetails && state?.eventPlayers && state?.scorecard) {
      return;
    }

    // If no eventId in URL, we can't fetch
    if (!eventId) {
      setError("No event ID provided");
      setLoading(false);
      return;
    }

    const fetchEventData = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/admin/events/${eventId}`);
        const eventData = response.data;

        setEventDetails({
          ...eventData.details,
          scorecard: eventData.scorecard,
          teeTime: eventData.details?.tee_time,
          numTimes: eventData.details?.num_teetimes,
          total_yardage: eventData.total_yardage,
        });

        // Normalize players
        const normalizedPlayers = (eventData.players || []).map(player => ({
          ...player,
          quota: player.player_quota || player.current_quota,
        }));
        setEventPlayers(normalizedPlayers);

        setScorecard(eventData.scorecard || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching event data:", err);
        setError("Failed to load event data");
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [searchParams, state]);

  useEffect(() => {
    // Dynamically update the @page rule for print orientation
    const styleTag = document.createElement("style");
    styleTag.type = "text/css";
    styleTag.id = "dynamic-print-style";
    styleTag.innerHTML = `
      @media print {
        @page {
          size: ${orientation};
        }
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      // Clean up the dynamically added style tag
      const existingStyleTag = document.getElementById("dynamic-print-style");
      if (existingStyleTag) {
        existingStyleTag.remove();
      }
    };
  }, [orientation]); // Re-run whenever orientation changes

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !eventDetails || !eventPlayers || !scorecard) {
    return <div className="p-4 text-red-600">{error || "No data provided for printing."}</div>;
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      {/* Orientation Selector */}
      <div className="p-4 flex space-x-4">
        <button
          onClick={() => setOrientation("landscape")}
          className={`px-4 py-2 rounded ${
            orientation === "landscape"
              ? "bg-blue-500 text-white"
              : "bg-gray-300 text-black"
          }`}
        >
          Landscape
        </button>
        <button
          onClick={() => setOrientation("portrait")}
          className={`px-4 py-2 rounded ${
            orientation === "portrait"
              ? "bg-blue-500 text-white"
              : "bg-gray-300 text-black"
          }`}
        >
          Portrait
        </button>
        <button
          onClick={handlePrint}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
        >
          Print
        </button>
      </div>

      {/* Content to Print */}
      <div id="print-content" className="p-4">
        <div className="mb-8">
          <ScorecardSheet
            eventDetails={eventDetails}
            players={eventPlayers}
            scorecard={scorecard}
          />
        </div>

        <div className="page-break">
          <PayoutTablePrint />
        </div>
      </div>
    </div>
  );
};

export default PrintScorecardPage;
